import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getGrievances, Grievance } from '../../services/grievance';

interface GrievanceFilters {
  dateRange: { from?: string; to?: string };
}

interface GrievanceState {
  grievances: Grievance[];
  selectedGrievance: Grievance | null;
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  searchQuery: string;
  filters: GrievanceFilters;
  error?: string;
  hasFetched: boolean; // track if data already fetched
}

const initialState: GrievanceState = {
  grievances: [],
  selectedGrievance: null,
  total: 0,
  loading: false,
  page: 1,
  pageSize: 10,
  searchQuery: '',
  filters: { dateRange: {} },
  hasFetched: false,
  sortBy: "createdAt",
  sortOrder: "desc",
};

export const fetchGrievancesThunk = createAsyncThunk<
  { data: Grievance[]; total: number },
  void,
  { state: { grievances: GrievanceState } }
>('grievances/fetchGrievances', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const { page, pageSize, searchQuery, filters, sortBy,
      sortOrder, } = state.grievances;
    const response = await getGrievances({
      page,
      pageSize,
      search: searchQuery,
      from: filters.dateRange.from,
      to: filters.dateRange.to,
      sortBy,
      sortOrder,
    });
    return { data: response.data || [], total: response.total || 0 };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

const grievanceSlice = createSlice({
  name: 'grievances',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilters: (state, action: PayloadAction<GrievanceFilters>) => {
      state.filters = action.payload;
      state.page = 1;
    },
    resetFilters: (state) => {
      state.filters = { dateRange: {} };
    },
    setSelectedGrievance: (state, action: PayloadAction<Grievance | null>) => {
      state.selectedGrievance = action.payload;
    },
    clearSelectedGrievance: (state) => {
      state.selectedGrievance = null;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGrievancesThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchGrievancesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.grievances = action.payload.data;
        state.total = action.payload.total;
        state.hasFetched = true;
      })
      .addCase(fetchGrievancesThunk.rejected, (state, action) => {
        state.loading = false;
        state.grievances = [];
        state.total = 0;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  setFilters,
  resetFilters,
  setSelectedGrievance,
  clearSelectedGrievance,
  setHasFetched,
} = grievanceSlice.actions;

export default grievanceSlice.reducer;
